import { useEffect, useState } from 'react';
import { API_BASE_URL } from '../../config/api';
import {
  analyzeUserTradingHistory,
  getLatestAnalysisResult,
  ParsedUserTradingAnalysis,
} from '../../services/aiAnalysisService';
import { invalidateMyPageCache } from '../../services/mypageService';
import './AIAnalysisSection.css';

interface AIAnalysisSectionProps {
  userKey: number;
  theme: 'light' | 'dark';
  tokenCount: number;
  tradingHistoryCount: number;
  onAnalysisComplete?: () => void;
  refreshTrigger?: number;
}

export const AIAnalysisSection = ({
  userKey,
  theme,
  tokenCount,
  tradingHistoryCount,
  onAnalysisComplete,
  refreshTrigger,
}: AIAnalysisSectionProps) => {
  const [analysis, setAnalysis] = useState<ParsedUserTradingAnalysis | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({
    tradingStyle: false,
    detailedAnalysis: false,
  });
  const [expandedSessionId, setExpandedSessionId] = useState<number | null>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // refreshTrigger가 변경될 때 최신 분석 결과 불러오기
  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      const loadLatestAnalysis = async () => {
        try {
          const latestResult = await getLatestAnalysisResult(API_BASE_URL, userKey, true);
          if (latestResult) {
            setAnalysis(latestResult);
            setError(null);
          }
        } catch (err: any) {
          console.error('최신 분석 결과 로드 실패:', err);
          // 에러가 발생해도 기존 분석 결과는 유지
        }
      };
      loadLatestAnalysis();
    }
  }, [refreshTrigger, userKey]);

  const handleAnalyze = async () => {
    if (tokenCount < 1) {
      setError('AI 분석 토큰이 부족합니다. 토큰을 획득한 후 다시 시도해주세요.');
      return;
    }

    if (tradingHistoryCount < 10) {
      setError('매매내역이 10개 이상 있어야 분석할 수 있습니다. 게임을 더 플레이해주세요.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setAnalysis(null);

    try {
      const result = await analyzeUserTradingHistory(API_BASE_URL, userKey, 10);
      setAnalysis(result);
      // 토큰 차감 반영: 마이페이지 캐시 무효화 후 새로고침 콜백 호출
      invalidateMyPageCache();
      if (onAnalysisComplete) {
        onAnalysisComplete();
      }
    } catch (err: any) {
      console.error('AI 분석 실패:', err);
      if (err.message?.includes('토큰이 부족')) {
        setError(err.message);
      } else {
        setError(
          err.message || 'AI 분석을 수행하는데 실패했습니다. 잠시 후 다시 시도해주세요.'
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formatNumber = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value !== 'number') return String(value);
    if (isNaN(value)) return 'N/A';
    return value.toFixed(2);
  };

  const formatCurrency = (value: number): string => {
    if (value >= 10000) {
      return `${(value / 10000).toFixed(0)}만원`;
    }
    return `${value.toLocaleString()}원`;
  };

  // 거래 0회인 세션 필터링
  const validSessions = analysis?.sessionSummaries.filter(
    (s) => s.statistics.totalTrades > 0
  ) || [];

  // 피드백 데이터 파싱 헬퍼
  const getFeedbackData = () => {
    if (!analysis?.feedback) return null;
    
    const fb = analysis.feedback as any;
    
    // 새로운 JSON 구조 파싱
    if (fb.trading_style_analysis || fb.money_management || fb.risk_management || fb.mentor_feedback) {
      return {
        tradingStyle: fb.trading_style_analysis?.type 
          ? `${fb.trading_style_analysis.type} - ${fb.trading_style_analysis.description || ''}`
          : fb.trading_style_analysis?.description,
        summary: fb.mentor_feedback?.summary,
        strengths: fb.mentor_feedback?.strengths || [],
        weaknesses: fb.mentor_feedback?.weaknesses || [],
        recommendations: fb.mentor_feedback?.action_plan || [],
        riskAssessment: fb.risk_management?.scary_explanation || fb.risk_management?.advice,
        moneyManagement: fb.money_management?.evaluation,
        consistency: fb.consistency_analysis?.evaluation,
        riskScore: fb.risk_management?.score,
        moneyScore: fb.money_management?.score,
        consistencyScore: fb.consistency_analysis?.score,
      };
    }
    
    // 기존 구조 지원
    return {
      tradingStyle: fb.tradingStyle,
      summary: fb.summary,
      strengths: fb.strengths || [],
      weaknesses: fb.weaknesses || [],
      recommendations: fb.recommendations || [],
      riskAssessment: fb.riskAssessment,
      statisticalInsights: fb.statisticalInsights,
    };
  };

  const feedbackData = getFeedbackData();

  // 스크롤 감지
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleSessionDetail = (historyId: number) => {
    setExpandedSessionId(expandedSessionId === historyId ? null : historyId);
  };

  return (
    <div className={`ai-analysis-section ${theme}`}>
      {/* 상단 헤더 */}
      <div className="page-header">
        <div className="page-title-group">
          <h1 className="page-title">AI 투자 분석</h1>
          <p className="page-subtitle">최근 매매내역을 종합 분석하여 투자 패턴을 파악합니다</p>
        </div>
        <div className={`token-indicator ${tokenCount >= 1 ? 'available' : 'empty'}`}>
          <span className="token-label">보유 토큰</span>
          <div className="token-value-group">
            <span className="coin-icon">🪙</span>
            <span className="token-value">{tokenCount}</span>
          </div>
        </div>
      </div>

      {/* 분석 시작 영역 */}
      {!analysis && (
        <div className="action-section">
          <button
            className={`primary-button ${
              tokenCount >= 1 && tradingHistoryCount >= 10 ? 'active' : 'disabled'
            }`}
            onClick={handleAnalyze}
            disabled={isLoading || tokenCount < 1 || tradingHistoryCount < 10}
          >
            {isLoading ? (
              <>
                <span className="spinner-small"></span>
                <span>분석 중...</span>
              </>
            ) : (
              <>
                <span className="button-icon">✨</span>
                <span>AI 분석 시작</span>
                <span className="button-arrow">→</span>
              </>
            )}
          </button>
          {tokenCount < 1 && (
            <p className="helper-text warning">
              토큰이 부족합니다. 게임을 플레이하거나 가입 보너스를 받아보세요.
            </p>
          )}
          {tokenCount >= 1 && tradingHistoryCount < 10 && (
            <p className="helper-text warning">
              매매내역이 {tradingHistoryCount}개입니다. 분석을 위해서는 최소 10개 이상의 매매내역이 필요합니다.
            </p>
          )}
        </div>
      )}

      {/* 에러 메시지 */}
      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
        </div>
      )}

      {/* Floating 다시 분석 버튼 */}
      {analysis && !isLoading && (
        <div className="floating-reanalyze-button">
          <button
            className={`primary-button ${
              tokenCount >= 1 && tradingHistoryCount >= 10 ? 'active' : 'disabled'
            }`}
            onClick={handleAnalyze}
            disabled={isLoading || tokenCount < 1 || tradingHistoryCount < 10}
          >
            {isLoading ? (
              <>
                <span className="spinner-small"></span>
                <span>분석 중...</span>
              </>
            ) : (
              <>
                <span className="button-icon">✨</span>
                <span>다시 분석</span>
                <span className="button-arrow">→</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* 분석 결과 */}
      {analysis && !isLoading && (
        <div className="analysis-results">
          {/* 주요 지표 */}
          <section className="metrics-section">
            <h2 className="section-title">주요 지표</h2>
            <div className="metrics-grid">
              <div className="metric-card metric-card-1">
                <div className="metric-label">
                  <span className="metric-icon">📊</span>
                  분석 세션
                </div>
                <div className="metric-value">{validSessions.length}개</div>
              </div>
              <div className="metric-card metric-card-2">
                <div className="metric-label">
                  <span className="metric-icon">🎯</span>
                  승률
                </div>
                <div className={`metric-value ${analysis.overallStatistics.averageWinRate >= 50 ? 'positive' : 'negative'}`}>
                  {formatNumber(analysis.overallStatistics.averageWinRate)}%
                </div>
              </div>
              <div className="metric-card metric-card-3">
                <div className="metric-label">
                  <span className="metric-icon">📈</span>
                  수익률
                </div>
                <div className={`metric-value ${analysis.overallStatistics.averageProfitRate >= 0 ? 'positive' : 'negative'}`}>
                  {analysis.overallStatistics.averageProfitRate >= 0 ? '+' : ''}
                  {formatNumber(analysis.overallStatistics.averageProfitRate)}%
                </div>
              </div>
              <div className="metric-card metric-card-4">
                <div className="metric-label">
                  <span className="metric-icon">💰</span>
                  수익
                </div>
                <div className={`metric-value ${analysis.overallStatistics.totalProfit >= 0 ? 'positive' : 'negative'}`}>
                  {formatCurrency(analysis.overallStatistics.totalProfit)}
                </div>
              </div>
            </div>
          </section>

          {/* AI 분석 결과 - 재구성된 우선순위 */}
          {feedbackData ? (
            <section className="feedback-section">
              <h2 className="section-title">📊 AI 분석 결과</h2>
              
              {/* 1. 종합 평가 점수 - 최상단 강조 */}
              {(feedbackData.riskScore !== undefined || feedbackData.moneyScore !== undefined || feedbackData.consistencyScore !== undefined) && (
                <div className="score-section-highlight">
                  <div className="score-grid-large">
                    {feedbackData.riskScore !== undefined && (
                      <div className="score-card-large">
                        <div className="score-icon">🛡️</div>
                        <div className="score-label-large">리스크 관리</div>
                        <div className="score-value-large">{feedbackData.riskScore}</div>
                        <div className="score-bar-large">
                          <div className="score-bar-fill-large" style={{ width: `${feedbackData.riskScore}%` }}></div>
                        </div>
                        <div className="score-subtitle">/ 100</div>
                      </div>
                    )}
                    {feedbackData.moneyScore !== undefined && (
                      <div className="score-card-large">
                        <div className="score-icon">💰</div>
                        <div className="score-label-large">자금 관리</div>
                        <div className="score-value-large">{feedbackData.moneyScore}</div>
                        <div className="score-bar-large">
                          <div className="score-bar-fill-large" style={{ width: `${feedbackData.moneyScore}%` }}></div>
                        </div>
                        <div className="score-subtitle">/ 100</div>
                      </div>
                    )}
                    {feedbackData.consistencyScore !== undefined && (
                      <div className="score-card-large">
                        <div className="score-icon">📈</div>
                        <div className="score-label-large">일관성</div>
                        <div className="score-value-large">{feedbackData.consistencyScore}</div>
                        <div className="score-bar-large">
                          <div className="score-bar-fill-large" style={{ width: `${feedbackData.consistencyScore}%` }}></div>
                        </div>
                        <div className="score-subtitle">/ 100</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* 2. 종합 평가 요약 */}
              {feedbackData.summary && (
                <div className="feedback-block feedback-block-summary">
                  <h3 className="feedback-block-title">📝 종합 평가</h3>
                  <p className="feedback-block-content">{feedbackData.summary}</p>
                </div>
              )}

              {/* 3. 핵심 인사이트 - 강점 & 개선점 */}
              {((feedbackData.strengths && feedbackData.strengths.length > 0) || 
                (feedbackData.weaknesses && feedbackData.weaknesses.length > 0)) && (
                <div className="feedback-row">
                  {feedbackData.strengths && feedbackData.strengths.length > 0 && (
                    <div className="feedback-block feedback-block-success">
                      <h3 className="feedback-block-title">✅ 강점</h3>
                      <ul className="feedback-list">
                        {feedbackData.strengths.map((strength: string, idx: number) => (
                          <li key={idx}>{strength}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {feedbackData.weaknesses && feedbackData.weaknesses.length > 0 && (
                    <div className="feedback-block feedback-block-warning">
                      <h3 className="feedback-block-title">⚠️ 개선점</h3>
                      <ul className="feedback-list">
                        {feedbackData.weaknesses.map((weakness: string, idx: number) => (
                          <li key={idx}>{weakness}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* 4. 상세 분석 - 아코디언으로 접기 */}
              <div className="accordion-section">
                <button
                  className="accordion-header"
                  onClick={() => toggleSection('detailedAnalysis')}
                >
                  <span className="accordion-title">📋 상세 분석</span>
                  <span className={`accordion-icon ${expandedSections.detailedAnalysis ? 'expanded' : ''}`}>▼</span>
                </button>
                {expandedSections.detailedAnalysis && (
                  <div className="accordion-content">
                    {/* 투자 스타일 */}
                    {feedbackData.tradingStyle && (
                      <div className="feedback-block feedback-block-info">
                        <h3 className="feedback-block-title">🎯 투자 스타일</h3>
                        <p className="feedback-block-content">{feedbackData.tradingStyle}</p>
                      </div>
                    )}

                    {/* 리스크 평가 */}
                    {feedbackData.riskAssessment && (
                      <div className="feedback-block feedback-block-danger">
                        <h3 className="feedback-block-title">⚠️ 리스크 평가</h3>
                        <p className="feedback-block-content">{feedbackData.riskAssessment}</p>
                      </div>
                    )}

                    {/* 자금 관리 평가 */}
                    {feedbackData.moneyManagement && (
                      <div className="feedback-block feedback-block-info">
                        <h3 className="feedback-block-title">💰 자금 관리</h3>
                        <p className="feedback-block-content">{feedbackData.moneyManagement}</p>
                      </div>
                    )}

                    {/* 일관성 평가 */}
                    {feedbackData.consistency && (
                      <div className="feedback-block feedback-block-info">
                        <h3 className="feedback-block-title">📈 일관성 분석</h3>
                        <p className="feedback-block-content">{feedbackData.consistency}</p>
                      </div>
                    )}

                    {/* 추천 사항 */}
                    {feedbackData.recommendations && feedbackData.recommendations.length > 0 && (
                      <div className="feedback-block feedback-block-info">
                        <h3 className="feedback-block-title">💡 개선 방안</h3>
                        <ul className="feedback-list">
                          {feedbackData.recommendations.map((rec: string, idx: number) => (
                            <li key={idx}>{rec}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </section>
          ) : (
            <section className="feedback-section">
              <div className="empty-feedback">
                <div className="empty-feedback-icon">📊</div>
                <h3 className="empty-feedback-title">분석 결과가 없습니다</h3>
                <p className="empty-feedback-message">
                  AI 분석 결과를 생성할 수 없었습니다. 다시 분석을 시도해주세요.
                </p>
              </div>
            </section>
          )}

          {/* 세션 요약 - 클릭 가능하게 개선 */}
          {validSessions.length > 0 && (
            <section className="sessions-section">
              <h2 className="section-title">📋 거래 세션 ({validSessions.length}개)</h2>
              <div className="sessions-list">
                {validSessions.map((summary, index) => {
                  const sessionColors = [
                    { border: '#3b82f6' },
                    { border: '#8b5cf6' },
                    { border: '#ec4899' },
                    { border: '#f59e0b' },
                    { border: '#10b981' },
                    { border: '#06b6d4' },
                    { border: '#f97316' },
                    { border: '#6366f1' },
                    { border: '#14b8a6' },
                    { border: '#a855f7' },
                  ];
                  const color = sessionColors[index % sessionColors.length];
                  const isExpanded = expandedSessionId === summary.historyId;
                  
                  return (
                    <div 
                      key={summary.historyId} 
                      className={`session-item ${isExpanded ? 'expanded' : ''}`}
                      style={{
                        borderLeftColor: color.border,
                        borderLeftWidth: '4px',
                      }}
                    >
                      <div 
                        className="session-header-clickable"
                        onClick={() => toggleSessionDetail(summary.historyId)}
                      >
                        <div className="session-header">
                          <div className="session-header-left">
                            <span className="session-index" style={{ color: color.border }}>
                              세션 {index + 1}
                            </span>
                            <span className="session-stock-code">
                              {summary.stockCode || '종목 정보 없음'}
                            </span>
                          </div>
                          <div className="session-header-right">
                            <span className={`session-profit ${summary.statistics.profitRate >= 0 ? 'positive' : 'negative'}`}>
                              {summary.statistics.profitRate >= 0 ? '+' : ''}
                              {formatNumber(summary.statistics.profitRate)}%
                            </span>
                            <span className={`session-expand-icon ${isExpanded ? 'expanded' : ''}`}>▼</span>
                          </div>
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="session-detail">
                          <div className="session-detail-grid">
                            <div className="session-detail-item">
                              <span className="session-detail-label">기간</span>
                              <span className="session-detail-value">{summary.dateRange}</span>
                            </div>
                            <div className="session-detail-item">
                              <span className="session-detail-label">총 거래 횟수</span>
                              <span className="session-detail-value">{summary.statistics.totalTrades}회</span>
                            </div>
                            <div className="session-detail-item">
                              <span className="session-detail-label">승률</span>
                              <span className="session-detail-value">{formatNumber(summary.statistics.winRate)}%</span>
                            </div>
                            <div className="session-detail-item">
                              <span className="session-detail-label">수익률</span>
                              <span className={`session-detail-value ${summary.statistics.profitRate >= 0 ? 'positive' : 'negative'}`}>
                                {summary.statistics.profitRate >= 0 ? '+' : ''}
                                {formatNumber(summary.statistics.profitRate)}%
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* 하단 액션 */}
          <div className="analysis-footer">
            <button
              className={`primary-button ${
                tokenCount >= 1 && tradingHistoryCount >= 10 ? 'active' : 'disabled'
              }`}
              onClick={handleAnalyze}
              disabled={isLoading || tokenCount < 1 || tradingHistoryCount < 10}
            >
              {isLoading ? (
                <>
                  <span className="spinner-small"></span>
                  <span>분석 중...</span>
                </>
              ) : (
                <>
                  <span className="button-icon">✨</span>
                  <span>다시 분석</span>
                  <span className="button-arrow">→</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* 맨 위로 버튼 */}
      {showScrollTop && (
        <button className="scroll-top-button" onClick={scrollToTop} title="맨 위로">
          ↑
        </button>
      )}
    </div>
  );
};
